import {
    getLocation,
    push,
    LOCATION_CHANGE,
    replace,
} from 'connected-react-router';
import { stringify, parse } from 'query-string';
import _ from 'lodash';
import genericStatsService from '../../services/genericStatsService';
import { setLoading, setErrorMessage } from './ui';
import { logout } from './user';
import { getDefaultFrom, getDefaultTo, getGroupingPeriod } from '../storeUtils/dateUtils';
import routeConstants from '../../Routes/routeConstants';
import { isUnauthorized } from '../../services/utils/responseValidator';

// ACTIONS
const SET_FORMATED_IMPRESSION_DATA = 'SET_FORMATED_IMPRESSION_DATA';
const GROUP_IMPRESSION_BY = 'GROUP_IMPRESSION_BY';
const CLEAR_IMPRESSION_DATA = 'CLEAR_IMPRESSION_DATA';
const CLEAR_IMPESSION_FORM = 'CLEAR_IMPESSION_FORM';
const SET_IMPRESSION_FORM_STATE = 'SET_IMPRESSION_FORM_STATE';
const SET_IMPRESSION_FORM_FIELD = 'SET_IMPRESSION_FORM_FIELD';
const SET_IMPRESSION_LOCATION = 'SET_IMPRESSION_LOCATION';
const SET_SELECTED_IMPRESSION_GRAPH_KEYS = 'SET_SELECTED_IMPRESSION_GRAPH_KEYS';

const initialState = {
    formState: {
        product: [],
        source: [],
        page: [],
        from: getDefaultFrom(),
        to: getDefaultTo(),
        isCustomer: false,
    },
    selectedGraphKeys: null,
    groupBy: 'MONTH',
    location: {
        pathname: routeConstants.IMPRESSION_STATS,
        hash: '',
        search: '',
    },
    stats: {},
};

// REDUCER
const reducer = (state = initialState, action) => {
    switch (action.type) {
    case SET_FORMATED_IMPRESSION_DATA: {
        const clone = _.cloneDeep(action.value);
        const {
            graphDataPoint,
            graphDate,
        } = action.value;

        return Object.assign({}, state, {
            stats: clone,
            graphDataPoint: graphDataPoint[state.groupBy.toLowerCase()],
            graphDate: graphDate[state.groupBy.toLowerCase()],
        });
    }
    case GROUP_IMPRESSION_BY: {
        const {
            graphDataPoint,
            graphDate,
        } = state.stats;

        return Object.assign({}, state, {
            groupBy: action.value,
            graphDataPoint: graphDataPoint && graphDataPoint[action.value.toLowerCase()],
            graphDate: graphDate && graphDate[action.value.toLowerCase()],
        });
    }
    case SET_IMPRESSION_FORM_STATE: {
        return Object.assign({}, state, {
            formState: action.value,
        });
    }
    case SET_IMPRESSION_FORM_FIELD: {
        return Object.assign({}, state, {
            formState: { ...state.formState, ...action.value },
        });
    }
    case SET_IMPRESSION_LOCATION: {
        return Object.assign({}, state, {
            location: action.value,
        });
    }
    case CLEAR_IMPRESSION_DATA: {
        return Object.assign({}, state, {
            stats: {},
            graphDataPoint: null,
            graphDate: null,
            selectedGraphKeys: null,
        });
    }
    case CLEAR_IMPESSION_FORM: {
        return Object.assign({}, state, {
            formState: initialState.formState,
            graphDataPoint: null,
            graphDate: null,
        });
    }
    case SET_SELECTED_IMPRESSION_GRAPH_KEYS: {
        return Object.assign({}, state, {
            selectedGraphKeys: action.value,
        });
    }
    case LOCATION_CHANGE: {
        const { pathname, state: actionPayloadState } = action.payload.location || {};
        const isImpressionStatsLocation = pathname === routeConstants.IMPRESSION_STATS;

        if (isImpressionStatsLocation) {
            const { impressionstats } = actionPayloadState || {};
            const {
                graphDataPoint,
                graphDate,
                selectedGraphKeys,
                formState,
                groupBy,
                stats,
            } = impressionstats || initialState;

            return Object.assign({}, state, {
                graphDataPoint,
                graphDate,
                selectedGraphKeys,
                location: action.payload.location,
                formState,
                groupBy,
                stats,
            });
        }
        return state;
    }
    default:
        return state;
    }
};

// ACTION CREATORS
const clearData = () => ({
    type: CLEAR_IMPRESSION_DATA,
});

const clearForm = () => ({
    type: CLEAR_IMPESSION_FORM,
});

const groupStatsBy = groupBy => ({
    type: GROUP_IMPRESSION_BY,
    value: groupBy,
});

const internalSetFormattedData = stats => ({
    type: SET_FORMATED_IMPRESSION_DATA,
    value: stats,
});

const setFormField = field => ({
    type: SET_IMPRESSION_FORM_FIELD,
    value: field,
});

const internalSetFormState = formData => ({
    type: SET_IMPRESSION_FORM_STATE,
    value: formData,
});

const internalSetLocation = location => ({
    type: SET_IMPRESSION_LOCATION,
    value: location,
});

const setSelectedGraphKeys = selected => ({
    type: SET_SELECTED_IMPRESSION_GRAPH_KEYS,
    value: selected,
});

// THUNK
const clearAll = () => (dispatch, getState) => {
    dispatch(clearData());
    dispatch(clearForm());
    const { formState } = getState().impressionstats;
    dispatch(push({
        pathname: routeConstants.IMPRESSION_STATS,
        search: stringify(formState),
        state: getState(),
    }));
};

const getImpressionStats = formData => (dispatch, getState) => {
    dispatch(setLoading(true));
    dispatch(clearData());
    dispatch(internalSetFormState(formData));
    genericStatsService.getImpressionStats(formData)
        .then((apiResonse) => {
            dispatch(groupStatsBy(getGroupingPeriod(formData.from, formData.to)));
            dispatch(internalSetFormattedData(apiResonse));
            dispatch(setLoading(false));
            dispatch(push({
                pathname: routeConstants.IMPRESSION_STATS,
                search: stringify(formData),
                state: getState(),
            }));
            dispatch(internalSetLocation(getLocation(getState())));
        })
        .catch((err) => {
            dispatch(setLoading(false));
            if (isUnauthorized(err.response)) {
                dispatch(logout());
            }
            dispatch(setErrorMessage(err.message));
        });
};

const setInitialImpressionState = location => (dispatch, getState) => {
    const { formState } = getState().leadsstats;
    const formParams = parse(location.search);

    const newFormState = {
        product: formParams.product || formState.product,
        source: formParams.source || formState.source,
        page: formParams.page || formState.page,
        to: formParams.to || formState.to,
        from: formParams.from || formState.from,
        isCustomer: !!formParams.isCustomer && formParams.isCustomer === 'true',
    };

    dispatch(internalSetFormState(newFormState));

    dispatch(replace({
        pathname: routeConstants.IMPRESSION_STATS,
        search: stringify(newFormState),
        state: getState(),
    }));

    dispatch(getImpressionStats(newFormState));
};

export {
    getImpressionStats,
    setInitialImpressionState,
    groupStatsBy,
    setFormField,
    clearData,
    clearForm,
    setSelectedGraphKeys,
    clearAll,
};

export default reducer;
